import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const axiosAuth = axios.create({ baseURL: API });

axiosAuth.interceptors.request.use((config) => {
  const token = localStorage.getItem('wtf_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axiosAuth.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    if (status === 401) {
      localStorage.removeItem('wtf_token');
      localStorage.removeItem('wtf_user');
      window.dispatchEvent(new Event('auth_expired'));
    }
    // Let callers handle specific errors via .catch()
    return Promise.reject(err);
  }
);

// Admin axios instance
const axiosAdmin = axios.create({ baseURL: API });
axiosAdmin.interceptors.request.use((config) => {
  const token = localStorage.getItem('wtf_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const api = {
  // Auth
  register: (data) => axios.post(`${API}/auth/register`, data).then(r => r.data),
  login: (data) => axios.post(`${API}/auth/login`, data).then(r => r.data),
  sendOTP: (data) => axios.post(`${API}/auth/send-otp`, data).then(r => r.data),
  verifyOTP: (data) => axios.post(`${API}/auth/verify-otp`, data).then(r => r.data),
  resendOTP: (data) => axios.post(`${API}/auth/resend-otp`, data).then(r => r.data),
  verifyLoginOTP: (data) => axios.post(`${API}/auth/verify-login-otp`, data).then(r => r.data),
  getMe: () => axiosAuth.get('/auth/me').then(r => r.data),
  saveOnboarding: (data) => axiosAuth.put('/auth/onboarding', data).then(r => r.data),
  forgotPassword: (identifier) => axios.post(`${API}/auth/forgot-password`, { identifier }).then(r => r.data),
  resetPassword: (token, new_password) => axios.post(`${API}/auth/reset-password`, { token, new_password }).then(r => r.data),
  updateProfile: (data) => axiosAuth.put('/auth/profile', data).then(r => r.data),
  changePassword: (data) => axiosAuth.put('/auth/change-password', data).then(r => r.data),
  recordAdvisoryConsent: () => axiosAuth.post('/auth/advisory-consent').then(r => r.data),
  suspendAccount: () => axiosAuth.post('/auth/suspend').then(r => r.data),
  deleteAccount: () => axiosAuth.delete('/auth/delete').then(r => r.data),
  toggleAutoRenew: () => axiosAuth.put('/auth/auto-renew').then(r => r.data),

  // Subscription
  getSubscriptionInfo: () => axiosAuth.get('/subscription/info').then(r => r.data),
  getSubscriptionPricing: () => axiosAuth.get('/subscription/pricing').then(r => r.data),
  createCheckout: (origin_url) => axiosAuth.post('/subscription/checkout', { origin_url }).then(r => r.data),
  checkPaymentStatus: (sessionId) => axiosAuth.get(`/subscription/status/${sessionId}`).then(r => r.data),
  getPaymentHistory: () => axiosAuth.get('/subscription/payment-history').then(r => r.data),
  getDailyDigest: () => axiosAuth.get('/content/daily-digest').then(r => r.data),
  cancelSubscription: () => axiosAuth.post('/subscription/cancel').then(r => r.data),
  getReceipt: (sessionId) => axiosAuth.get(`/subscription/receipt/${sessionId}`).then(r => r.data),

  // Content (auth-aware for dismissed filtering)
  getFeed: (category, region, limit = 20, content_type) => {
    const token = localStorage.getItem('wtf_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { limit, category, region };
    if (content_type) params.content_type = content_type;
    return axios.get(`${API}/feed`, { params, headers }).then(r => r.data);
  },
  getPersonalizedFeed: (region) => {
    const params = {};
    if (region && region !== 'all') params.region = region;
    return axiosAuth.get('/feed/personalized', { params }).then(r => r.data);
  },
  getExplanation: (topicId) => {
    const token = localStorage.getItem('wtf_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return axios.get(`${API}/explanation/${topicId}`, { headers }).then(r => r.data);
  },
  getOverlay: (topicId) => {
    const token = localStorage.getItem('wtf_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return axios.get(`${API}/overlay/${topicId}`, { headers }).then(r => r.data);
  },
  explain: (input) => axiosAuth.post('/explain', { input }).then(r => r.data),
  getTrending: () => axios.get(`${API}/trending`).then(r => r.data),
  saveTopic: (topicId) => axiosAuth.post(`/save/${topicId}`).then(r => r.data),
  getSaved: () => axiosAuth.get('/saved').then(r => r.data),
  reactToTopic: (topicId, emoji) => axiosAuth.post(`/react/${topicId}`, { emoji }).then(r => r.data),
  getMyReactions: (topicIds) => axiosAuth.get('/reactions/mine', { params: { topic_ids: topicIds.join(',') } }).then(r => r.data),
  dismissTopic: (topicId) => axiosAuth.post(`/dismiss/${topicId}`).then(r => r.data),
  getRenderCard: (topicId, templateType = 'standard') =>
    axios.get(`${API}/render-card/${topicId}`, { params: { template_type: templateType } }).then(r => r.data),
  refreshTrending: () => axios.post(`${API}/refresh-trending`).then(r => r.data),
  getWeather: (lat, lon) => axios.get(`${API}/weather`, { params: { lat, lon } }).then(r => r.data),

  // Admin
  adminLogin: (data) => axios.post(`${API}/admin/login`, data).then(r => r.data),
  adminGetStats: () => axiosAdmin.get('/admin/stats').then(r => r.data),
  adminGetUsers: () => axiosAdmin.get('/admin/users').then(r => r.data),
  adminUpdateUserStatus: (userId, status) =>
    axiosAdmin.put(`/admin/users/${userId}/status?status=${status}`).then(r => r.data),
  adminGetPrompts: () => axiosAdmin.get('/admin/prompts').then(r => r.data),
  adminUpdatePrompt: (promptId, data) => axiosAdmin.put(`/admin/prompts/${promptId}`, data).then(r => r.data),
  adminRunPrompt: (promptId) => axiosAdmin.post(`/admin/prompts/${promptId}/run`).then(r => r.data),
  adminGetTopics: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== undefined) qs.append(k, v); });
    return axiosAdmin.get(`/admin/topics?${qs.toString()}`).then(r => r.data);
  },
  adminCreateTopic: (data) => axiosAdmin.post('/admin/topics', data).then(r => r.data),
  adminUpdateTopic: (topicId, data) => axiosAdmin.put(`/admin/topics/${topicId}`, data).then(r => r.data),
  adminDeleteTopic: (topicId) => axiosAdmin.delete(`/admin/topics/${topicId}`).then(r => r.data),

  // Admin - Scheduler & Publisher
  adminTriggerJob: (jobId) => axiosAdmin.post(`/admin/trigger-job/${jobId}`).then(r => r.data),
  adminGetScheduler: () => axiosAdmin.get('/admin/scheduler').then(r => r.data),
  adminGetPublished: () => axiosAdmin.get('/admin/published').then(r => r.data),
  adminPublishNow: () => axiosAdmin.post('/admin/publish-now').then(r => r.data),
  adminGetFeedMonitor: () => axiosAdmin.get('/admin/feed-monitor').then(r => r.data),
  adminGetUsersRevenue: () => axiosAdmin.get('/admin/users/revenue-summary').then(r => r.data),
  adminGetUsersBehaviour: () => axiosAdmin.get('/admin/users/behaviour-summary').then(r => r.data),
  adminGetUsersRecent: (limit = 5) => axiosAdmin.get(`/admin/users/recent?limit=${limit}`).then(r => r.data),
  getSchedulerStatus: () => axios.get(`${API}/scheduler/status`).then(r => r.data),

  // Admin - Dedup Logs
  adminGetDedupLogs: (limit = 30) => axiosAdmin.get(`/admin/dedup-logs?limit=${limit}`).then(r => r.data),
  adminGetDedupLogDetail: (logId) => axiosAdmin.get(`/admin/dedup-logs/${logId}`).then(r => r.data),
  adminDeployCheck: () => axiosAdmin.get('/admin/deploy-check').then(r => r.data),
  adminDeployLogs: (limit = 20) => axiosAdmin.get(`/admin/deploy-logs?limit=${limit}`).then(r => r.data),
  adminDownloadDataSources: () => axiosAdmin.get('/admin/data-sources-csv', { responseType: 'blob' }).then(r => r.data),

  // Pulse — Score Engines
  getPulseTraders: (params = {}) => axiosAuth.get('/pulse/traders', { params }).then(r => r.data),
  getPulseInvestors: (params = {}) => axiosAuth.get('/pulse/investors', { params }).then(r => r.data),
  getPulseStats: () => axiosAuth.get('/pulse/stats').then(r => r.data),
  seedPulseTraders: () => axiosAuth.post('/pulse/traders/seed').then(r => r.data),
  seedPulseInvestors: () => axiosAuth.post('/pulse/investors/seed').then(r => r.data),
  getPulseNews: (params = {}) => axiosAuth.get('/pulse/news', { params }).then(r => r.data),
  runPulseNews: () => axiosAuth.post('/pulse/run/news').then(r => r.data),
  getPulseTopHighlights: () => axiosAuth.get('/pulse/highlights').then(r => r.data),

  // Trends v2 — What's Hot Pipeline
  getTrendsFeed: (persona, category = 'all', params = {}) =>
    axiosAuth.get(`/trends/feed/${persona}/${category}`, { params }).then(r => r.data),
  getTrendsEntity: (canonicalId) =>
    axiosAuth.get(`/trends/entity/${canonicalId}`).then(r => r.data),
  getTrendsHighlights: (limit = 9) =>
    axiosAuth.get(`/trends/highlights?limit=${limit}`).then(r => r.data),
  getTrendsStats: () => axiosAuth.get('/trends/stats').then(r => r.data),
  getIndiaBullionToday: () => axiosAuth.get('/trends/india-bullion-today').then(r => r.data),
  getCommoditiesToday: () => axiosAuth.get('/trends/commodities-today').then(r => r.data),
  getForexMajorsToday: (home) => axiosAuth.get('/trends/forex-majors-today', { params: home ? { home } : {} }).then(r => r.data),
  getBettingMoversToday: () => axiosAuth.get('/trends/betting-movers-today').then(r => r.data),

  // Analytics — lightweight page-visit tracking
  trackPageVisit: (payload) => axiosAuth.post('/analytics/page-visit', payload).then(r => r.data).catch(() => null),

  // Portfolio — premium daily 9-card digest
  getPortfolioOptions: () => axiosAuth.get('/portfolio/options').then(r => r.data),
  getPortfolio: () => axiosAuth.get('/portfolio').then(r => r.data),
  upsertPortfolio: (body) => axiosAuth.put('/portfolio', body).then(r => r.data),
  deletePortfolio: () => axiosAuth.delete('/portfolio').then(r => r.data),
  getPortfolioFeed: () => axiosAuth.get('/portfolio/feed').then(r => r.data),
  getPortfolioUniverse: (params = {}) => axiosAuth.get('/portfolio/universe', { params }).then(r => r.data),
  getPortfolioQuotes: () => axiosAuth.get('/portfolio/quotes').then(r => r.data),
  onboardHoldings: (tracked_entities) =>
    axiosAuth.post('/portfolio/onboard-holdings', { tracked_entities }).then(r => r.data),

  // Global entity search (FB-PERS-3)
  searchEntities: (q, limit = 20) =>
    axiosAuth.get('/search', { params: { q, limit } }).then(r => r.data),
  suggestEntities: (q, asset_type, limit = 10) =>
    axiosAuth.get('/search/suggest', { params: { q, asset_type, limit } }).then(r => r.data),

  // Today's Pulse hero (FB-HOOK-1) + viewer counter (FB-SOC-1)
  getTodaysPulse: () => axiosAuth.get('/portfolio/today').then(r => r.data),
  trackEntityView: (entity_key, session_id) =>
    axiosAuth.post('/portfolio/track-view', { entity_key, session_id }).then(r => r.data),

  // Two-mode holdings onboarding (FB-PERS-1.1)
  getResellerUniverse: (q = '', limit = 40) =>
    axiosAuth.get('/portfolio/reseller-universe', { params: { q, limit } }).then(r => r.data),
  requestEntity: (kind, name, region, note) =>
    axiosAuth.post('/portfolio/request-entity', { kind, name, region, note }).then(r => r.data),
  onboardHoldingsTwoMode: (modes, tracked_entities) =>
    axiosAuth.post('/portfolio/onboard-holdings', { modes, tracked_entities }).then(r => r.data),
  // Tracked-entities v2 (Iter 61d — search-chip portfolio UX)
  getTrackedV2: () => axiosAuth.get('/portfolio/tracked-v2').then(r => r.data),
  saveTrackedV2: (body) => axiosAuth.put('/portfolio/tracked-v2', body).then(r => r.data),
  getTrackedV2Feed: () => axiosAuth.get('/portfolio/tracked-v2/feed').then(r => r.data),
  runTrendsPipeline: () => axiosAuth.post('/trends/run').then(r => r.data),
};

export default api;
